import { useLayoutEffect, type ReactNode } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { ReduxProvider } from "lib/store/ReduxProvider";
import { useStore } from "lib/store/store";
import ThemeSettings from "components/ThemeSettings";
import { Toaster } from "components/ui/sonner";
import HomePage from "pages/HomePage";
import {
  normalizeGuideTab,
  normalizePath,
  pageFromPathname,
  persistPage,
  resolvePageFromLocation,
} from "lib/routes";

/** Keep Redux `page` in sync with the URL on every navigation and hard reload. */
function PathSync({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { setPage, setFrontTab } = useStore();

  useLayoutEffect(() => {
    const path = normalizePath(location.pathname);
    const fromPath = pageFromPathname(path);

    if (fromPath) {
      setPage(fromPath);
      persistPage(fromPath);
      return;
    }

    if (path !== "/") return;

    const params = new URLSearchParams(location.search);
    const tabParam = params.get("tab") ?? params.get("tabs");
    const guideTab = normalizeGuideTab(tabParam);

    if (guideTab) {
      setPage("front");
      persistPage("front");
      setFrontTab(guideTab);
      return;
    }

    setPage(resolvePageFromLocation("/", location.search));
  }, [location.pathname, location.search, setPage, setFrontTab]);

  return <>{children}</>;
}

function RootRedirects() {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get("tab") ?? searchParams.get("tabs");
  const guideTab = normalizeGuideTab(tabParam);

  if (tabParam === "support") return <Navigate to="/support" replace />;
  if (tabParam === "demos") return <Navigate to="/demos" replace />;
  if (tabParam === "offers" || tabParam === "trending") {
    return <Navigate to="/offers" replace />;
  }
  if (tabParam === "manage-tools" || tabParam === "manage" || tabParam === "login") {
    return <Navigate to="/manage-tools" replace />;
  }
  if (tabParam === "more-tools" || tabParam === "toolkit") {
    return <Navigate to="/more-tools" replace />;
  }

  // Dashboard guide tabs: stay on `/` and open the matching FrontPage panel
  // (?tab=unsubscribe | tg-explore | tg-explorer | activate | activation)
  if (guideTab) {
    return <HomePage />;
  }

  return <HomePage />;
}

function AppRoutes() {
  return (
    <PathSync>
      <Routes>
        <Route path="/" element={<RootRedirects />} />
        <Route path="/manage-tools" element={<HomePage />} />
        <Route path="/login" element={<Navigate to="/manage-tools" replace />} />
        <Route path="/manage" element={<Navigate to="/manage-tools" replace />} />
        <Route path="/more-tools" element={<HomePage />} />
        <Route path="/toolkit" element={<Navigate to="/more-tools" replace />} />
        <Route path="/demos" element={<HomePage />} />
        <Route path="/offers" element={<HomePage />} />
        <Route path="/support" element={<HomePage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </PathSync>
  );
}

export default function App() {
  return (
    <ReduxProvider>
      <BrowserRouter>
        <AppRoutes />
        <ThemeSettings />
        <Toaster />
      </BrowserRouter>
    </ReduxProvider>
  );
}
